import { useCallback, useLayoutEffect, useEffect, useRef, useState } from "react";
import {
	observeResearchInOurDNA,
	disconnectResearchInOurDNAObserver
} from "utils/ddiff-animations/researchInourDNAObserver";
import { DDiffResearchIsInOurDNAProps, Tile } from "./types/ddiff-research-is-in-our-DNA-types";
import { getDecodedHtml } from "utils/getDecodedHtml";
import {
	isContentMax,
	isDesktopRange,
	isTablet,
	isMobile
} from "components/utility/page-responsive/PageResponsive";
import DDiffResearchIsInOurDNATileRow from "./DDiffResearchIsInOurDNATileRow";

const DDiffResearchIsInOurDNA = ({ data }: DDiffResearchIsInOurDNAProps) => {
	const headingText = data?.headingText;
	const tiles: Tile[] = data?.tiles;
	const [singleRowView, setSingleRowView] = useState<boolean>(false);
	const [updatedBreakpoint, setUpdatedBreakpoint] = useState<
		"contentMax" | "medium" | "mobile" | ""
	>("");
	const [viewPortSize, setViewPortSize] = useState(window.innerWidth);
	const [tileRowTop, setTileRowTop] = useState<Tile[]>([]);
	const [tileRowBottom, setTileRowBottom] = useState<Tile[]>([]);
	const componentRef = useRef<null | HTMLDivElement>(null);
	const titleRef = useRef<HTMLDivElement | null>(null);
	const tileRowBottomRef = useRef<HTMLDivElement | null>(null);
	let throttleTimer = useRef<boolean>(false);

	const throttle = useCallback((callback: Function, time: number) => {
		if (throttleTimer.current) return;
		throttleTimer.current = true;
		setTimeout(() => {
			callback();
			throttleTimer.current = false;
		}, time);
	}, []);

	useEffect(() => {
		// Prepare tiles data for two row layout
		if (tiles) {
			setTileRowTop(tiles.slice(0, 2));
			setTileRowBottom(tiles.slice(2, tiles.length));
		}

		if (isContentMax()) {
			setSingleRowView(true);
		}
		window.addEventListener("resize", () => {
			if (isContentMax()) {
				setSingleRowView(true);
			} else {
				setSingleRowView(false);
			}
		});
		return () => {
			window.removeEventListener("resize", () => {
				if (isContentMax()) {
					setSingleRowView(true);
				} else {
					setSingleRowView(false);
				}
			});
		};
	}, [tiles]);

	useEffect(() => {
		const setBreakpoint = () => {
			// nav bar on ios causes resize event to trigger on scroll,
			// this check prevents functions from running if there was NOT a size change
			if (viewPortSize !== window.innerWidth) {
				setViewPortSize(window.innerWidth);
				if (isContentMax()) {
					setUpdatedBreakpoint("contentMax");
				} else if (isDesktopRange() || isTablet()) {
					if (updatedBreakpoint !== "medium") {
						setUpdatedBreakpoint("medium");
					}
				} else if (isMobile()) {
					setUpdatedBreakpoint("mobile");
				}
			}
		};
		window.addEventListener("resize", setBreakpoint);
		return () => {
			window.removeEventListener("resize", setBreakpoint);
		};
	}, [updatedBreakpoint, viewPortSize]);

	useLayoutEffect(() => {
		let intersectionEls: Array<HTMLElement> = [];
		if (tileRowBottomRef.current && titleRef.current) {
			intersectionEls = [titleRef.current, tileRowBottomRef.current];
		}
		const initializeIntersectionObervations = () => {
			if (isDesktopRange() || isTablet() || updatedBreakpoint === "medium") {
				observeResearchInOurDNA(intersectionEls);
			} else if (isMobile() || updatedBreakpoint === "mobile") {
				disconnectResearchInOurDNAObserver();
			}
		};

		initializeIntersectionObervations();

		// On breakpoint change, set animations to last frame instead of animating on
		const setAllAnimationsToLastFrame = () => {
			const animationParent = componentRef?.current?.closest(".ddiff-split-content");

			if (!animationParent?.classList.contains("ddiff-split-paragraph-animate")) {
				animationParent?.classList.add("ddiff-split-paragraph-animate");
			}
			if (
				animationParent?.classList.contains("ddiff-split-paragraph-animate") &&
				componentRef.current
			) {
				const descendentEls = componentRef.current.querySelectorAll<HTMLElement>("*");
				descendentEls.forEach((el) => {
					el.style.animationDuration = "0ms";
					el.style.animationDelay = "0ms";
				});
			}
		};
		if (updatedBreakpoint === "contentMax") {
			setAllAnimationsToLastFrame();
		}
		if (updatedBreakpoint === "medium") {
			setAllAnimationsToLastFrame();
		}

		window.addEventListener("resize", () => throttle(initializeIntersectionObervations, 100));

		return () => {
			window.removeEventListener("resize", () => throttle(initializeIntersectionObervations, 100));
			disconnectResearchInOurDNAObserver();
		};
	}, [updatedBreakpoint, throttle]);

	return (
		<div className="ddiff-research-in-dna-container" ref={componentRef}>
			<div
				className="title t-label-sm-heavy tc-cool-gray-01 ddiff-research-md-animation"
				ref={titleRef}
				dangerouslySetInnerHTML={{ __html: getDecodedHtml(headingText) }}
			/>
			<div className="tiles-comp-container">
				{singleRowView ? (
					<DDiffResearchIsInOurDNATileRow tiles={tiles} updatedBreakpoint={updatedBreakpoint} />
				) : (
					<>
						<DDiffResearchIsInOurDNATileRow
							tiles={tileRowTop}
							updatedBreakpoint={updatedBreakpoint}
						/>
						<DDiffResearchIsInOurDNATileRow
							ref={tileRowBottomRef}
							tiles={tileRowBottom}
							updatedBreakpoint={updatedBreakpoint}
						/>
					</>
				)}
				<div className="line-wipe-container"></div>
			</div>
		</div>
	);
};

export default DDiffResearchIsInOurDNA;
